/**
 * AI Management Tab - AI-Powered Sales Management Tools
 * Phase 7 - Connected to Real Backend APIs
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Psychology as AIIcon,
  School as CoachingIcon,
  Assignment as AssignmentIcon,
  ShowChart as ForecastIcon
} from '@mui/icons-material';
import CoachingRecommendationsPanel from '../components/analytics/CoachingRecommendationsPanel';
import SmartLeadAssignment from '../components/analytics/SmartLeadAssignment';
import PerformancePredictions from '../components/analytics/PerformancePredictions';
import { useAllAIManagement, useAssignLead } from '../hooks/useAnalytics';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const AIManagementTab: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  // Fetch all AI management data from real backend APIs
  const {
    performanceTable,
    coachingRecommendations,
    leadAssignments,
    predictions,
    isLoading,
    isError
  } = useAllAIManagement();

  // Lead assignment mutation
  const assignLead = useAssignLead();

  // Show loading state
  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  // Show error state
  if (isError) {
    const errorDetails = [
      performanceTable.error ? `Performance Table: ${(performanceTable.error as Error).message}` : null,
      coachingRecommendations.error ? `Coaching: ${(coachingRecommendations.error as Error).message}` : null,
      leadAssignments.error ? `Lead Assignments: ${(leadAssignments.error as Error).message}` : null,
      predictions.error ? `Predictions: ${(predictions.error as Error).message}` : null,
    ].filter(Boolean);

    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load AI management data. Please make sure you are logged in.
        </Alert>
        {errorDetails.length > 0 && (
          <Alert severity="info">
            <Typography variant="body2">Error details:</Typography>
            {errorDetails.map((err, i) => (
              <Typography key={i} variant="caption" display="block">{err}</Typography>
            ))}
          </Alert>
        )}
      </Container>
    );
  }

  // Extract data with fallbacks
  const coachingData = coachingRecommendations.data || [];
  const assignmentData = leadAssignments.data || [];
  const predictionsData = predictions.data || { predictions: [], goals: [] };

  // Handle lead assignment
  const handleAssignLead = async (leadId: string, salesRepId: string) => {
    try {
      await assignLead.mutateAsync({ leadId, salesRepId });
      // Success handled by mutation
    } catch (error) {
      console.error('Failed to assign lead:', error);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // REMOVED: All mock data - now using real API data
  // Using coachingData, assignmentData, predictionsData from API hooks above

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          <AIIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          AI Management Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          AI-powered insights and recommendations for sales team management
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
          <Tab
            icon={<CoachingIcon />}
            label="Coaching Recommendations"
            iconPosition="start"
          />
          <Tab
            icon={<AssignmentIcon />}
            label="Smart Lead Assignment"
            iconPosition="start"
          />
          <Tab
            icon={<ForecastIcon />}
            label="Performance Predictions"
            iconPosition="start"
          />
        </Tabs>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <CoachingRecommendationsPanel
                  recommendations={coachingData}
                  loading={false}
                />
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <SmartLeadAssignment
                  suggestions={assignmentData}
                  loading={false}
                  onAssign={handleAssignLead}
                />
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <PerformancePredictions
                  predictions={predictionsData.predictions}
                  goals={predictionsData.goals}
                  loading={false}
                  timeframe="Next Month"
                />
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default AIManagementTab;
